import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { applyTheme } from "../utils/theme";
import Sidebar from "../components/common/Sidebar";
import Navbar from "../components/common/Navbar";
import DataTable from "../components/common/DataTable";
import { FaSearch, FaChevronDown } from "react-icons/fa";
import SideModal from "../components/common/SideModal";
import { useApi } from "../hooks/useApi";
import { message } from "antd";

// Columns will be defined inside the component to access translation function



// Tabs will be defined inside component to access translation function

const ProviderManagement = () => {
  const { t } = useTranslation();
  const dropdownRef = useRef(null);
  const [activeTab, setActiveTab] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [providers, setProviders] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [searchOption, setSearchOption] = useState("name");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTrigger, setSearchTrigger] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const { getProviders, approveProvider } = useApi();
  const mode = useSelector((state) => state.theme.mode);

  // Define tabs inside component to access translation function
  const tabs = [
    t('tabs.all'),
    t('tabs.approved'),
    t('tabs.pending'),
    t('tabs.rejected')
  ];

  // Define columns inside component to access translation function
  const columns = [
    {
      key: "fullName",
      title: t('table.name'),
      render: (val) => <span className="text-text-tablebody font-semibold">{val}</span>,
    },
    {
      key: "email",
      title: t('table.email'),
      render: (val) => <span className="text-text-tablebody">{val}</span>,
    },
    {
      key: "category",
      title: t('table.category'),
      render: (val) => <span className="text-text-tablebody">{val || "N/A"}</span>,
    },
    {
      key: "isApproved",
      title: t('table.status'),
      render: (val) => (
        <span
          className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
            val === true
              ? "bg-[#55CE631A] text-[#55CE63]"
              : val === null
              ? "bg-[#FFBC341A] text-[#FFBC34]"
              : val === false
              ? "bg-[#F62D511A] text-[#F62D51]"
              : "bg-gray-200 text-gray-600"
          }`}
        >
          {val === true ? "Approved" : val === null ? "Pending" : val === false ? "Rejected" : "Unknown"}
        </span>
      ),
    },
    {
      key: "verified",
      title: t('table.verified'),
      render: (val) => (
        <span
          className={`inline-block px-3 py-1 rounded text-xs font-semibold ${
            val === true
              ? "bg-green-100 text-green-600"
              : "bg-red-100 text-red-600"
          }`}
        >
          {val === true ? "Yes" : "No"}
        </span>
      ),
    },
    {
      key: "action",
      title: t('table.action'),
    },
  ];

  useEffect(() => {
    applyTheme(mode);
  }, [mode]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch providers on component mount and when pagination changes
  useEffect(() => {
    const fetchProviders = async () => {
      try {
        // Build search parameters based on search option and term
        const searchParams = {};
        if (searchTerm.trim()) {
          switch (searchOption) {
            case "name":
              searchParams.name = searchTerm.trim();
              break;
            case "email":
              searchParams.email = searchTerm.trim();
              break;
            default:
              break;
          }
        }

        // Add status filter parameter if set
        if (statusFilter) {
          searchParams.status = statusFilter;
        }

        const response = await getProviders(pagination.currentPage, pagination.itemsPerPage, searchParams);
        
        console.log('Providers API response:', response);
        console.log('Response.data:', response.data);
        console.log('Response.data type:', typeof response.data);
        console.log('Is response.data array?', Array.isArray(response.data));
        
        if (response.success) {
          // Handle the actual API response structure
          let providersData = [];
          if (response.data && response.data.docs && Array.isArray(response.data.docs)) {
            providersData = response.data.docs;
          } else if (Array.isArray(response.data)) {
            providersData = response.data;
          } else if (Array.isArray(response.result)) {
            providersData = response.result;
          } else {
            console.warn('No valid providers array found in response:', response);
            providersData = [];
          }
          
          console.log('Setting providers to:', providersData);
          setProviders(providersData);
          setPagination(prev => ({
            ...prev,
            totalPages: response.data?.totalPages || 1,
            totalItems: response.data?.totalDocs || 0,
          }));
        } else {
          message.error(response.message || "Failed to fetch providers");
        }
      } catch (error) {
        message.error("Error fetching providers");
        console.error("Error fetching providers:", error);
      }
    };

    fetchProviders();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.itemsPerPage, searchOption, searchTrigger, statusFilter, getProviders]);

  // Handler for opening modal with row data
  const handleView = (row) => {
    setSelectedProvider(row);
    setModalOpen(true);
  };

  // Handler for approving a provider
  const handleApprove = async (providerId) => {
    try {
      const response = await approveProvider(providerId, "approve");
      
      if (response.success) {
        message.success("Provider approved successfully!");
        
        // Update the specific provider in the providers array
        setProviders(prevProviders => 
          prevProviders.map(provider => 
            provider._id === providerId 
              ? { ...provider, isApproved: true }
              : provider
          )
        );
        
        // If the modal is open and we're approving the selected provider, update it too
        if (selectedProvider && selectedProvider._id === providerId) {
          setSelectedProvider(prev => ({ ...prev, isApproved: true }));
        }
      } else {
        message.error(response.message || "Failed to approve provider");
      }
    } catch (error) {
      message.error("Error approving provider");
      console.error("Error approving provider:", error);
    }
  };

  // Handler for rejecting a provider
  const handleReject = async (providerId) => {
    try {
      const response = await approveProvider(providerId, "decline");
      
      if (response.success) {
        message.success("Provider rejected successfully!");
        
        // Update the specific provider in the providers array
        setProviders(prevProviders => 
          prevProviders.map(provider => 
            provider._id === providerId 
              ? { ...provider, isApproved: false }
              : provider
          )
        );
        
        // If the modal is open and we're rejecting the selected provider, update it too
        if (selectedProvider && selectedProvider._id === providerId) {
          setSelectedProvider(prev => ({ ...prev, isApproved: false }));
        }
      } else {
        message.error(response.message || "Failed to reject provider");
      }
    } catch (error) {
      message.error("Error rejecting provider");
      console.error("Error rejecting provider:", error);
    }
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    setPagination(prev => ({
      ...prev,
      currentPage: page,
    }));
  };

  const handleSearchInputChange = (value) => {
    setSearchTerm(value);
    // Don't trigger search on input change, only update the state
  };

  const handleSearch = () => {
    // Reset to first page when searching
    setPagination(prev => ({
      ...prev,
      currentPage: 1,
    }));
    // Trigger search by updating searchTrigger
    setSearchTrigger(prev => prev + 1);
  };

  const handleSearchOptionChange = (option) => {
    setSearchOption(option);
    setDropdownOpen(false);
    setSearchTerm(""); // Clear search when changing option
  };

  const getSearchOptionLabel = (option) => {
    switch (option) {
      case "name": return t('search.byName');
      case "email": return t('search.byEmail');
      default: return t('search.byName');
    }
  };

  // Function to get status parameter based on tab index
  const getStatusFromTab = (tabIndex) => {
    switch (tabIndex) {
      case 0: return ""; // All - empty status
      case 1: return "approved"; // Approved
      case 2: return "pending"; // Pending
      case 3: return "declined"; // Rejected
      case 4: return "inactive"; // Deactivated
      default: return "";
    }
  };

  // No need for client-side filtering since we're doing server-side filtering
  const filteredProviders = providers;

  // Columns with updated View button
  const columnsWithView = columns.map((col) => {
    if (col.key === "action") {
      return {
        ...col,
        render: (_, row) => (
          <div className="flex justify-center gap-4 items-center">
            <button
              className="text-text-tableactionbtn hover:underline"
              onClick={() => handleView(row)}
            >
              {t('table.view')}
            </button>
            {(() => {
              const status = (typeof row.isApproved === 'string')
                ? row.isApproved.toLowerCase()
                : (row.isApproved === true
                  ? 'approved'
                  : row.isApproved === null
                    ? 'pending'
                    : row.isApproved === false
                    ? 'rejected' 
                    : 'unknown');

              return (
                <>
                  {(status === 'pending') && (
                    <>
                      <button 
                        className="text-[#00B050] hover:underline"
                        onClick={() => handleApprove(row._id)}
                      >
                        {t('table.approve')}
                      </button>
                      <button 
                        className="text-[#FF0000] hover:underline"
                        onClick={() => handleReject(row._id)}
                      >
                        {t('table.reject')}
                      </button>
                    </>
                  )}
                  {(status === 'approved') && (
                    <button 
                    className="text-[#FF0000] hover:underline"
                    onClick={() => handleReject(row._id)}
                    >
                      {t('table.reject')}
                    </button>
                  )}
                  {(status === 'rejected') && (
                    <button 
                      className="text-[#00B050] hover:underline"
                      onClick={() => handleApprove(row._id)}
                    >
                      {t('table.approve')}
                    </button>
                  )}
                </>
              );
            })()}
          </div>
        ),
      };
    }
    return col;
  });

  return (
    <div className="min-h-screen bg-background-main">
      <Navbar 
        onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        isMenuOpen={isMobileMenuOpen}
      />
      <div className="flex flex-col lg:flex-row">
        <Sidebar 
          isOpen={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
        />
        <main className="flex-1 p-4 lg:p-6 xl:p-8 pt-4 w-full overflow-x-auto">
          {/* Header and Filters */}
          <div className="flex flex-col gap-2 sm:flex-row sm:gap-0 justify-start mb-4">
            <div className="flex items-center bg-background-search rounded-full sm:rounded-l-full sm:rounded-r-none px-4 py-2 w-full sm:max-w-sm border border-border-search">
              <FaSearch 
                className="text-[#81879D] mr-2 cursor-pointer hover:text-accent transition-colors" 
                onClick={handleSearch}
              />
              <input
                type="text"
                placeholder={(() => {
                  switch (searchOption) {
                    case "name": return t('search.searchName');
                    case "email": return t('search.searchEmail');
                    default: return t('search.searchName');
                  }
                })()}
                value={searchTerm}
                onChange={(e) => handleSearchInputChange(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
                className="bg-transparent outline-none flex-1 text-input-text placeholder-[#81879D]"
              />
            </div>
            <div className="relative flex items-center bg-background-search rounded-full sm:rounded-r-full sm:rounded-l-none px-4 py-2 w-full sm:w-auto sm:min-w-[120px] border-l border-l-border-search sm:border-l-searchbar-divider border-r border-r-border-search border-y border-y-border-search cursor-pointer" onClick={() => setDropdownOpen(!dropdownOpen)}>
              <span className="text-[#A9AEBC] mr-2">{getSearchOptionLabel(searchOption)}</span>
              <FaChevronDown className={`text-[#81879D] ml-auto transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
              
              {/* Dropdown Options */}
              {dropdownOpen && (
                <div className="absolute top-full left-0 right-0 bg-background-search text-[#A9AEBC] rounded-b-lg shadow-lg z-10 mt-1">
                  <div 
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                    onClick={() => handleSearchOptionChange("name")}
                  >
                    {t('search.byName')}
                  </div>
                  <div 
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                    onClick={() => handleSearchOptionChange("email")}
                  >
                    {t('search.byEmail')}
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* Tabs */}
          <div className="flex mt-2">
            {tabs.map((tab, idx) => (
              <button
                key={tab}
                className={`px-2 lg:px-6 py-2 text-sm font-semibold border-t-2 transition-all duration-200 ${
                  activeTab === idx
                    ? "text-text-activetab border-border-activetab bg-background-activetab"
                    : "text-[#676D75] border-transparent bg-background-filter"
                }`}
                onClick={() => {
                  setActiveTab(idx);
                  const newStatus = getStatusFromTab(idx);
                  setStatusFilter(newStatus);

                  console.log('New status:', newStatus);
                  // Reset to first page when changing status filter
                  setPagination(prev => ({
                    ...prev,
                    currentPage: 1,
                  }));
                }}
              >
                {tab}
              </button>
            ))}
          </div>
          {/* Data Table */}
          <div className="bg-background-card rounded-b-lg shadow-md">
            <DataTable columns={columnsWithView} data={filteredProviders} />
          </div>
          {/* Pagination */}
          <div className="flex items-center justify-between mt-6 text-text-primary text-sm">
            <span>
              {t('pagination.showing')} {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1}-{Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} {t('pagination.of')} {pagination.totalItems}
            </span>
            <div className="flex items-center gap-1">
              <button
                className={`px-3 py-1 rounded ${
                  pagination.currentPage === 1
                    ? "bg-background-card border border-border-paginationbtn text-input-placeholder cursor-not-allowed"
                    : "bg-background-card border border-border-paginationbtn text-accent hover:bg-accent hover:text-white"
                }`}
                disabled={pagination.currentPage === 1}
                onClick={() => handlePageChange(1)}
              >
                {t('pagination.first')}
              </button>
              <button
                className={`px-3 py-1 rounded ${
                  pagination.currentPage === 1
                    ? "bg-background-card border border-border-paginationbtn text-input-placeholder cursor-not-allowed"
                    : "bg-background-card border border-border-paginationbtn text-accent hover:bg-accent hover:text-white"
                }`}
                disabled={pagination.currentPage === 1}
                onClick={() => handlePageChange(pagination.currentPage - 1)}
              >
                {t('pagination.back')}
              </button>
              
              {/* Page numbers */}
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                const pageNum = i + 1;
                return (
                  <button
                    key={pageNum}
                    className={`px-3 py-1 rounded ${
                      pagination.currentPage === pageNum
                        ? "bg-accent text-white font-bold"
                        : "bg-background-card border border-border-paginationbtn hover:bg-accent hover:text-white"
                    }`}
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
              
              {pagination.totalPages > 5 && (
                <>
                  <span className="px-2">...</span>
                  <button
                    className="px-3 py-1 rounded bg-background-card border border-border-paginationbtn hover:bg-accent hover:text-white"
                    onClick={() => handlePageChange(pagination.totalPages)}
                  >
                    {pagination.totalPages}
                  </button>
                </>
              )}
              
              <button
                className={`px-3 py-1 rounded ${
                  pagination.currentPage === pagination.totalPages
                    ? "bg-background-card border border-border-paginationbtn text-input-placeholder cursor-not-allowed"
                    : "bg-background-card border border-border-paginationbtn text-accent hover:bg-accent hover:text-white"
                }`}
                disabled={pagination.currentPage === pagination.totalPages}
                onClick={() => handlePageChange(pagination.currentPage + 1)}
              >
                {t('pagination.next')}
              </button>
              <button
                className={`px-3 py-1 rounded ${
                  pagination.currentPage === pagination.totalPages
                    ? "bg-background-card border border-border-paginationbtn text-input-placeholder cursor-not-allowed"
                    : "bg-background-card border border-border-paginationbtn text-accent hover:bg-accent hover:text-white"
                }`}
                disabled={pagination.currentPage === pagination.totalPages}
                onClick={() => handlePageChange(pagination.totalPages)}
              >
                {t('pagination.last')}
              </button>
            </div>
          </div>
        </main>
      </div>
      {/* Side Modal for Provider Details */}
      <SideModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={t('providerDetail.title')}
        actions={
          selectedProvider && (
            <>
              {(() => {
                const status = (typeof selectedProvider.isApproved === 'string')
                  ? selectedProvider.isApproved.toLowerCase()
                  : (selectedProvider.isApproved === true
                    ? 'approved'
                    : selectedProvider.isApproved === false
                      ? 'pending'
                      : 'unknown');

                return (
                  <>
                    {(status === 'pending') && (
                      <>
                        <button
                          className="w-full py-3 rounded-xl bg-[#02B856] text-white font-semibold text-lg"
                          onClick={() => handleApprove(selectedProvider._id)}
                        >
                          {t('providerDetail.approve')}
                        </button>
                        <div className="flex gap-3 mt-2">
                          <button
                            className="w-1/2 py-3 rounded-xl text-text-primary font-semibold text-lg bg-transparent"
                            onClick={() => alert("Deactivated!")}
                          >
                            {t('providerDetail.deactivate')}
                          </button>
                          <button
                            className="w-1/2 py-3 rounded-xl border border-[#FF4D4F] text-[#FF4D4F] font-semibold text-lg bg-transparent"
                            onClick={() => handleReject(selectedProvider._id)}
                          >
                            {t('providerDetail.reject')}
                          </button>
                        </div>
                      </>
                    )}
                    {(status === 'approved') && (
                      <div className="flex gap-3">
                        <button
                          className="w-1/2 py-3 rounded-xl text-text-primary font-semibold text-lg bg-transparent"
                          onClick={() => alert("Deactivated!")}
                        >
                          {t('providerDetail.deactivate')}
                        </button>
                        <button
                          className="w-1/2 py-3 rounded-xl border border-[#FF4D4F] text-[#FF4D4F] font-semibold text-lg bg-transparent"
                          onClick={() => handleReject(selectedProvider._id)}
                        >
                          {t('providerDetail.reject')}
                        </button>
                      </div>
                    )}
                    {(status === 'rejected') && (
                      <button
                        className="w-full py-3 rounded-xl bg-[#02B856] text-white font-semibold text-lg"
                        onClick={() => handleApprove(selectedProvider._id)}
                      >
                        {t('providerDetail.approve')}
                      </button>
                    )}
                  </>
                );
              })()}
            </>
          )
        }
      >
        {selectedProvider && (
          <div>
            <h3 className="text-lg font-semibold mb-4 text-text-primary">
              {t('providerDetail.information')}
            </h3>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <div className="font-medium text-text-primary">
                  {t('providerDetail.name')} :
                </div>
                <div className="text-text-secondary">
                  {selectedProvider.fullName}
                </div>
              </div>
              <div>
                <div className="font-medium text-text-primary">
                  {t('providerDetail.email')} :
                </div>
                <div className="text-text-secondary">
                  {selectedProvider.email}
                </div>
              </div>
              <div>
                <div className="font-medium text-text-primary">
                  {t('providerDetail.phone')} :
                </div>
                <div className="text-text-secondary">
                  {selectedProvider.phoneNo || "N/A"}
                </div>
              </div>
              <div>
                <div className="font-medium text-text-primary">
                  {t('providerDetail.dateRegistered')} :
                </div>
                <div className="text-text-secondary">
                  {new Date(selectedProvider.createdAt).toLocaleDateString()}
                </div>
              </div>
              <div>
                <div className="font-medium text-text-primary">{t('providerDetail.categories')} :</div>
                <div className="text-text-secondary">
                  {selectedProvider.categories && selectedProvider.categories.length > 0 
                    ? selectedProvider.categories.join(", ") 
                    : t('providerDetail.noCategories')}
                </div>
              </div>
              <div>
                <div className="font-medium text-text-primary">{t('providerDetail.location')} :</div>
                <div className="text-text-secondary">
                  {selectedProvider.latitude && selectedProvider.longitude 
                    ? `${selectedProvider.latitude.toFixed(4)}, ${selectedProvider.longitude.toFixed(4)}`
                    : t('providerDetail.noLocation')}
                </div>
              </div>
              <div>
                <div className="font-medium text-text-primary">{t('providerDetail.status')} :</div>
                <div>
                  <span
                    className={`inline-block px-3 py-1 rounded text-xs font-semibold ${
                      selectedProvider.isApproved === true
                        ? "bg-green-100 text-green-600"
                        : selectedProvider.isApproved === false
                        ? "bg-yellow-100 text-yellow-600"
                        : "bg-gray-200 text-gray-600"
                    }`}
                  >
                    {selectedProvider.isApproved === true ? "Approved" : selectedProvider.isApproved === false ? "Pending" : "Unknown"}
                  </span>
                </div>
              </div>
              <div>
                <div className="font-medium text-text-primary">{t('providerDetail.verified')} :</div>
                <div>
                  <span
                    className={`inline-block px-3 py-1 rounded text-xs font-semibold ${
                      selectedProvider.verified === true
                        ? "bg-green-100 text-green-600"
                        : "bg-red-100 text-red-600"
                    }`}
                  >
                    {selectedProvider.verified === true ? "Yes" : "No"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </SideModal>
    </div>
  );
};

export default ProviderManagement;
